import { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Trash, 
  ArrowLeft, 
  CreditCard, 
  Check,
  ShoppingCart,
  ArrowRight
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [cart, setCart] = useState(() => {
    // Handle cart items from navigation state
    const cartItems = location.state?.cartItems || [];
    return cartItems;
  });
  const [step, setStep] = useState(1); // 1: Review, 2: Info, 3: Payment, 4: Confirmation
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const totalPrice = cart.reduce((sum, item) => sum + item.price, 0);

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step === 2) {
      setStep(3);
    } else if (step === 3) {
      setIsProcessing(true);
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      setIsProcessing(false);
      setStep(4);
      toast.success('تم إتمام الطلب بنجاح!');
    }
  };

  if (cart.length === 0 && step !== 4) {
    return (
      <main className="pt-20 min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <div className="text-center px-6">
          <div className="w-20 h-20 rounded-full bg-[#F3EFE9] flex items-center justify-center mx-auto mb-6">
            <ShoppingCart size={40} className="text-[#7A8A62]" />
          </div>
          <h2 className="text-2xl font-bold text-[#1A210D] font-['IBM_Plex_Sans_Arabic'] mb-4">
            سلة التسوق فارغة
          </h2>
          <p className="text-[#7A8A62] mb-6">لم تقم بإضافة أي منتجات بعد</p>
          <Link to="/products">
            <Button className="bg-[#2C3814] hover:bg-[#3C4A20] text-[#FAFAF7] rounded-xl px-6 py-3">
              تصفح المنتجات
            </Button>
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="checkout-page" className="pt-20 min-h-screen bg-[#FAFAF7]">
      {/* Header */}
      <section className="py-8 bg-[#F3EFE9] border-b border-[#D5D1C8]">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-3xl font-bold text-[#1A210D] font-['IBM_Plex_Sans_Arabic'] text-center">
            {step === 4 ? 'تم الطلب بنجاح' : 'إتمام الطلب'}
          </h1>
          
          {/* Progress Steps */}
          {step < 4 && (
            <div className="flex items-center justify-center gap-4 mt-8">
              {[
                { num: 1, label: 'المراجعة' },
                { num: 2, label: 'البيانات' },
                { num: 3, label: 'الدفع' },
              ].map((s, index) => (
                <div key={s.num} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                    step >= s.num 
                      ? 'bg-[#2C3814] text-[#FAFAF7]' 
                      : 'bg-[#D5D1C8] text-[#7A8A62]'
                  }`}>
                    {step > s.num ? <Check size={20} /> : s.num}
                  </div>
                  <span className={`mr-2 text-sm ${step >= s.num ? 'text-[#2C3814]' : 'text-[#7A8A62]'}`}>
                    {s.label}
                  </span>
                  {index < 2 && (
                    <div className={`w-12 h-0.5 mx-3 ${step > s.num ? 'bg-[#2C3814]' : 'bg-[#D5D1C8]'}`} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Step 1: Review Cart */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="bg-[#FAFAF7] rounded-3xl border border-[#D5D1C8] overflow-hidden">
                <div className="p-6 border-b border-[#D5D1C8]">
                  <h2 className="text-xl font-bold text-[#1A210D] font-['IBM_Plex_Sans_Arabic']">
                    مراجعة الطلب ({cart.length} منتج)
                  </h2>
                </div>
                
                <div className="divide-y divide-[#D5D1C8]">
                  {cart.map((item) => (
                    <div key={item.id} className="p-6 flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span 
                            className="px-2 py-1 rounded-lg text-xs font-medium"
                            style={{ backgroundColor: `${item.stageColor}15`, color: item.stageColor }}
                          >
                            {item.stageName}
                          </span>
                        </div>
                        <h3 className="font-semibold text-[#1A210D] mb-1">{item.name}</h3>
                        <p className="text-sm text-[#7A8A62]">{item.nameEn}</p>
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-[#2C3814] mb-2">
                          {item.price.toLocaleString()} ر.س
                        </div>
                        <button
                          data-testid={`remove-item-${item.id}`}
                          onClick={() => removeFromCart(item.id)}
                          className="text-red-500 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition-colors"
                        >
                          <Trash size={20} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="p-6 bg-[#F3EFE9]">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-[#7A8A62]">المجموع</span>
                    <span className="text-2xl font-bold text-[#2C3814]">
                      {totalPrice.toLocaleString()} ر.س
                    </span>
                  </div>
                  <Button
                    data-testid="proceed-to-info"
                    onClick={() => setStep(2)}
                    className="w-full bg-[#2C3814] hover:bg-[#3C4A20] text-[#FAFAF7] rounded-xl py-4 text-lg"
                  >
                    متابعة
                    <ArrowLeft size={20} className="mr-2 rtl-flip" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Customer Info */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <form onSubmit={handleSubmit}>
                <div className="bg-[#FAFAF7] rounded-3xl border border-[#D5D1C8] p-8">
                  <h2 className="text-xl font-bold text-[#1A210D] font-['IBM_Plex_Sans_Arabic'] mb-6">
                    بيانات العميل
                  </h2>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">الاسم الكامل *</Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="checkout-name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="rounded-xl h-12 bg-[#F3EFE9] border-[#D5D1C8]"
                        placeholder="أدخل اسمك الكامل"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">البريد الإلكتروني *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="checkout-email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="rounded-xl h-12 bg-[#F3EFE9] border-[#D5D1C8]"
                        placeholder="example@email.com"
                        dir="ltr"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">رقم الهاتف *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        data-testid="checkout-phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="rounded-xl h-12 bg-[#F3EFE9] border-[#D5D1C8]"
                        placeholder="+966 XX XXX XXXX"
                        dir="ltr"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="organization">اسم الجهة</Label>
                      <Input
                        id="organization"
                        name="organization"
                        data-testid="checkout-organization"
                        value={formData.organization}
                        onChange={handleInputChange}
                        className="rounded-xl h-12 bg-[#F3EFE9] border-[#D5D1C8]"
                        placeholder="اسم المركز أو الجهة"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 mt-8">
                    <Button
                      type="button"
                      onClick={() => setStep(1)}
                      variant="outline"
                      className="flex-1 border-[#D5D1C8] text-[#2C3814] rounded-xl py-4"
                    >
                      <ArrowRight size={20} className="ml-2 rtl-flip" />
                      رجوع
                    </Button>
                    <Button
                      type="submit"
                      data-testid="proceed-to-payment"
                      className="flex-1 bg-[#2C3814] hover:bg-[#3C4A20] text-[#FAFAF7] rounded-xl py-4"
                    >
                      متابعة للدفع
                      <ArrowLeft size={20} className="mr-2 rtl-flip" />
                    </Button>
                  </div>
                </div>
              </form>
            </motion.div>
          )}

          {/* Step 3: Payment */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <form onSubmit={handleSubmit}>
                <div className="bg-[#FAFAF7] rounded-3xl border border-[#D5D1C8] p-8">
                  <h2 className="text-xl font-bold text-[#1A210D] font-['IBM_Plex_Sans_Arabic'] mb-6">
                    معلومات الدفع
                  </h2>

                  {/* Order Summary */}
                  <div className="bg-[#F3EFE9] rounded-2xl p-6 mb-6">
                    <h3 className="font-semibold text-[#1A210D] mb-4">ملخص الطلب</h3>
                    {cart.map((item) => (
                      <div key={item.id} className="flex justify-between text-sm mb-2">
                        <span className="text-[#3C4A20]">{item.name}</span>
                        <span className="text-[#1A210D]">{item.price.toLocaleString()} ر.س</span>
                      </div>
                    ))}
                    <div className="border-t border-[#D5D1C8] mt-4 pt-4 flex justify-between">
                      <span className="font-bold text-[#1A210D]">المجموع</span>
                      <span className="font-bold text-[#2C3814] text-xl">
                        {totalPrice.toLocaleString()} ر.س
                      </span>
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div className="space-y-4">
                    <Label>طريقة الدفع</Label>
                    <div className="grid gap-4">
                      <div className="flex items-center gap-4 p-4 border-2 border-[#2C3814] rounded-xl bg-[#2C3814]/5">
                        <CreditCard size={24} className="text-[#2C3814]" />
                        <div>
                          <span className="font-medium text-[#1A210D]">بطاقة ائتمانية</span>
                          <span className="text-xs text-[#7A8A62] block">Visa, Mastercard, Mada</span>
                        </div>
                        <Check size={20} className="text-[#2C3814] mr-auto" />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4 mt-8">
                    <Button
                      type="button"
                      onClick={() => setStep(2)}
                      variant="outline"
                      className="flex-1 border-[#D5D1C8] text-[#2C3814] rounded-xl py-4"
                    >
                      <ArrowRight size={20} className="ml-2 rtl-flip" />
                      رجوع
                    </Button>
                    <Button
                      type="submit"
                      data-testid="complete-payment"
                      disabled={isProcessing}
                      className="flex-1 bg-[#2C3814] hover:bg-[#3C4A20] text-[#FAFAF7] rounded-xl py-4"
                    >
                      {isProcessing ? 'جاري المعالجة...' : `ادفع ${totalPrice.toLocaleString()} ر.س`}
                    </Button>
                  </div>
                </div>
              </form>
            </motion.div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="bg-[#FAFAF7] rounded-3xl border border-[#D5D1C8] p-12">
                <div className="w-20 h-20 rounded-full bg-[#4A5D23] flex items-center justify-center mx-auto mb-6">
                  <Check size={40} className="text-[#FAFAF7]" />
                </div>
                <h2 className="text-3xl font-bold text-[#1A210D] font-['IBM_Plex_Sans_Arabic'] mb-4">
                  شكراً لك!
                </h2>
                <p className="text-lg text-[#7A8A62] mb-8">
                  تم استلام طلبك بنجاح. سنتواصل معك قريباً على البريد الإلكتروني المسجل.
                </p>
                <div className="bg-[#F3EFE9] rounded-2xl p-6 mb-8 text-right">
                  <p className="text-sm text-[#7A8A62] mb-2">رقم الطلب</p>
                  <p className="font-mono text-lg text-[#2C3814] font-bold">
                    ORD-{Date.now().toString().slice(-8)}
                  </p>
                </div>
                <div className="flex gap-4 justify-center">
                  <Link to="/">
                    <Button
                      variant="outline"
                      className="border-[#D5D1C8] text-[#2C3814] rounded-xl px-6 py-3"
                    >
                      العودة للرئيسية
                    </Button>
                  </Link>
                  <Link to="/products">
                    <Button className="bg-[#2C3814] hover:bg-[#3C4A20] text-[#FAFAF7] rounded-xl px-6 py-3">
                      تصفح المزيد
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </main>
  );
};

export default CheckoutPage;
